import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { API } from '@/App';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle2 } from 'lucide-react';

/**
 * Free-plan signup modal. Triggered from PricingPage (Sign Up Free CTA + Start
 * for Free banner). Calls POST /api/auth/signup which sends a magic-link
 * verification email; on success we replace the form with a check-your-inbox
 * confirmation.
 */
export default function SignupModal({ open, onOpenChange, planId = 'nps_free' }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setForm({ name: '', email: '', password: '', confirm: '' });
    setError(''); setSuccess(false); setSubmitting(false);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim()) { setError('All fields are required.'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (form.password !== form.confirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name.trim(), email: form.email.trim(), password: form.password, plan_id: planId }),
      });
      if (res.ok) { setSuccess(true); }
      else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Signup failed. Please try again.');
      }
    } catch { setError('Network error — please try again.'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[440px]" data-testid="signup-modal">
        <div className="flex justify-center pt-2 pb-1">
          <img
            src="https://customer-assets.emergentagent.com/job_restaurant-voice/artifacts/s8cmwdge_satisFIND%2020th%20Logo.png"
            alt="satisFIND"
            style={{ width: 140, height: 'auto' }}
            data-testid="signup-modal-logo"
          />
        </div>
        <DialogHeader>
          <DialogTitle className="font-['Montserrat'] text-[#0F172A] text-center">
            {success ? 'Check your inbox' : 'Create your account'}
          </DialogTitle>
        </DialogHeader>
        {success ? (
          <div className="text-center space-y-3 py-4" data-testid="signup-success">
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#22C55E]" />
            <p className="text-sm text-[#0F172A]">
              Check your inbox to verify your email and activate your account.
            </p>
            <p className="text-xs text-[#64748B]">
              We sent a verification link to <span className="font-medium text-[#0F172A]">{form.email}</span>.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
            <div className="space-y-2">
              <Label htmlFor="signup-name">Full Name</Label>
              <Input id="signup-name" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Doe" data-testid="signup-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-email">Email</Label>
              <Input id="signup-email" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com" autoComplete="email" data-testid="signup-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-password">Password</Label>
              <Input id="signup-password" type="password" required minLength={8} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="At least 8 characters" autoComplete="new-password" data-testid="signup-password-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-confirm">Confirm Password</Label>
              <Input id="signup-confirm" type="password" required minLength={8} value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                placeholder="Re-enter password" autoComplete="new-password" data-testid="signup-confirm-input" />
            </div>
            {error && (
              <p className="text-sm text-[#DC2626] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2"
                 data-testid="signup-error">{error}</p>
            )}
            <Button type="submit" disabled={submitting}
              className="w-full bg-[#1A3E6F] hover:bg-[#122c50] text-white font-semibold"
              data-testid="signup-submit-btn">
              {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</>) : 'Create Account'}
            </Button>
            <p className="text-xs text-center text-[#64748B]">
              Already have an account?{' '}
              <Link to="/auth" className="text-[#1A3E6F] hover:underline font-medium">Log in →</Link>
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
